import type { BlockStateUnionType } from '@unifyapps/defs/types/block';
import type { RepeatableInfo, RuntimeSourceInfo } from './attachInstances';
import type { RepeatableBlockStateType } from '@unifyapps/defs/types/blocks';

// Only these runtime-source component types expose `collectChildStates` in their content.
// Any other componentType is treated as opted-out (defensive — should not appear in
// runtimeSourceInfo, since the precomputation already filters to runtime-source blocks).
const RUNTIME_SOURCE_COMPONENT_TYPES_WITH_COLLECT_CHILD_STATES = new Set([
  'Repeatable',
  'TreeView',
  'Carousel',
  'SwipeableStack',
]);

// Reads `collectChildStates` directly off the runtime block state's content.
// Runtime state's `content` mirrors `block.component.content` post-materialize
// (see materializeInstanceState + getBlockInitialState), so this is the same
// signal as the authoring-level `isCollectingChildStatesEnabledForBlock`
// predicate, just operating on what's actually available at compute-context
// build time.
function isCollectChildStatesEnabledForState(state: BlockStateUnionType | undefined): boolean {
  if (!state) return false;
  const componentType = (state as RepeatableBlockStateType).componentType;
  if (!componentType) return false;
  if (!RUNTIME_SOURCE_COMPONENT_TYPES_WITH_COLLECT_CHILD_STATES.has(componentType)) {
    return false;
  }
  const content = (state as { content?: { collectChildStates?: unknown } }).content;
  return content?.collectChildStates === true;
}

// Filters `runtimeSourceInfo` to only entries whose parent block has
// `collectChildStates=true`. The `.instances` Proxy attachment in
// `attachInstances` is the dominant cost on heavy Repeatable pages and is
// only useful for bindings that read `<repeatable>.instances[N].…`.
// Pages where no Repeatable opts in pay zero `attachInstances` cost.
//
// Returns `undefined` when the filtered map would be empty, so the caller
// can short-circuit the entire `attachInstances` call (saves the helper
// allocation + grouping pass).
//
// O(M + C) where M is the number of Repeatables and C is the number of
// children. The outer parent walk is the cheap dimension on typical pages
// (M << C), and the inner child walk is unavoidable since each surviving
// child must be re-keyed into the filtered output.
export function filterRuntimeSourceInfoByCollectChildStates(
  runtimeSourceInfo: RuntimeSourceInfo,
  blocksState: Record<string, BlockStateUnionType | undefined>,
): RuntimeSourceInfo | undefined {
  const { childToRepeatable, repeatables } = runtimeSourceInfo;

  const filteredRepeatables: Record<string, RepeatableInfo | undefined> = {};
  let anyOptedIn = false;
  for (const repeatableId of Object.keys(repeatables)) {
    const info = repeatables[repeatableId];
    if (!info) continue;
    if (!isCollectChildStatesEnabledForState(blocksState[repeatableId])) continue;
    filteredRepeatables[repeatableId] = info;
    anyOptedIn = true;
  }
  if (!anyOptedIn) return undefined;

  const filteredChildToRepeatable: Record<string, string | undefined> = {};
  let anyChildKept = false;
  for (const childId of Object.keys(childToRepeatable)) {
    const parentId = childToRepeatable[childId];
    if (parentId && filteredRepeatables[parentId]) {
      filteredChildToRepeatable[childId] = parentId;
      anyChildKept = true;
    }
  }
  // Preserve the `return undefined` short-circuit so
  // ContextBuilder.attachInstancesToBlocksState can skip the entire
  // attachInstances call when no children survive the filter (saves an
  // Object.keys + the helper allocation).
  if (!anyChildKept) return undefined;

  return {
    childToRepeatable: filteredChildToRepeatable,
    repeatables: filteredRepeatables,
  };
}
